/**
 * The two sides, kept deliberately apart.
 *
 * `side` is who you are: durable, set on the Case tab, and the only side that
 * ever authors a document. `panelLens` is how you are currently reading the
 * panel: transient, defaults to your side, and flips freely so you can ask "how
 * does the other side see this juror?" without ever generating the other side's
 * work product.
 */

import type { Side } from '@/types';
import { store } from '@/app/store';
import { byId } from '@/ui/dom';
import { showToast } from '@/ui/overlays';
import { currentPageId } from '@/ui/router';
import { renderPanel } from './panel';

const title = (side: Side): string => (side === 'defense' ? 'Defense' : 'Prosecution');


/**
 * Repaint everything that echoes either side, so the two can never drift apart
 * on screen. Called after any change to `side` or `panelLens`.
 */
function renderSideChrome(): void {
  const s = store.getState();

  // Case tab: which side you are. These buttons track identity, not the lens.
  const def = byId('btn-def');
  if (def) def.className = 'side-btn' + (s.side === 'defense' ? ' active-def' : '');
  const pros = byId('btn-pros');
  if (pros) pros.className = 'side-btn' + (s.side === 'prosecution' ? ' active-pros' : '');
  const label = byId('side-label');
  if (label) label.textContent = 'You are ' + title(s.side) + ' counsel';

  // Panel tab: the lens.
  const borrowed = s.panelLens !== s.side;
  const lensDef = byId('btn-lens-def');
  if (lensDef) lensDef.className = 'side-btn' + (s.panelLens === 'defense' ? ' active-def' : '');
  const lensPros = byId('btn-lens-pros');
  if (lensPros) lensPros.className = 'side-btn' + (s.panelLens === 'prosecution' ? ' active-pros' : '');
  const lensNote = byId('lens-note');
  if (lensNote) {
    lensNote.textContent = borrowed
      ? 'Reading as ' + title(s.panelLens) + '. Voir dire and reports are still written for the ' +
        title(s.side) + '.'
      : 'Reading as ' + title(s.side) + ' — your own side.';
    lensNote.className = borrowed ? 'lens-note borrowed' : 'lens-note';
  }

  // The header echo shows the LENS, because that is the value that moves. It
  // spells the side out in full — "PROS" was too easily read as an abbreviation
  // of nothing in particular, and this badge is the only thing telling you whose
  // eyes you are reading the panel through.
  //
  // When the lens is not your own side the badge goes amber. No "view" suffix
  // and no icon: the masthead has ~90px at iPhone width and "PROSECUTION" alone
  // needs nearly all of it. Colour carries the distinction here; the sentence
  // that explains it lives on the panel's own lens note.
  const indicator = byId('side-indicator');
  if (indicator) {
    indicator.textContent = title(borrowed ? s.panelLens : s.side);
    indicator.className = borrowed ? 'borrowed' : '';
  }
}

/**
 * Set the side you practise for. Resets the panel lens to match: changing who
 * you are should never leave you reading through the old side's lens.
 */
export function setSide(side: Side): void {
  const s = store.getState();
  s.side = side;
  s.panelLens = side;

  renderSideChrome();
  store.save();
  if (currentPageId() === 'panel') renderPanel();
  showToast('You are now ' + title(side) + ' counsel');
}

/**
 * Flip the lens the panel is read through. Never touches `side`, so nothing
 * changes author. Deliberately does not `store.save()` — the lens is transient,
 * and main.ts resets it to `side` on launch.
 */
export function setPanelLens(lens: Side): void {
  const s = store.getState();
  if (s.panelLens === lens) return;
  s.panelLens = lens;

  renderSideChrome();
  if (currentPageId() === 'panel') renderPanel();
  showToast(
    lens === s.side
      ? 'Back to your own view'
      : 'Reading as ' + title(lens) + ' — documents unchanged'
  );
}

/** Drop any borrowed lens and read as yourself again. */
export function resetPanelLens(): void {
  const s = store.getState();
  s.panelLens = s.side;
  renderSideChrome();
}
